import { useState, useEffect } from "react";
import { Company } from "../../types/schema";
import { formatPhoneNumber } from "../../utils/phoneFormat";
import { getUrl } from "aws-amplify/storage";
import "../../css/UserEditModal.css";
import "../../css/SiteAdminModals.css";

interface EditCompanyProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    company: Company | null;
    onSave: (data: Partial<Company>, logoFile?: File | null) => void;
}

export const EditCompanyProfileModal = ({ isOpen, onClose, company, onSave }: EditCompanyProfileModalProps) => {
    const [form, setForm] = useState<Partial<Company>>({});
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>("");

    useEffect(() => {
        if (company) {
            setForm({ ...company });
            setLogoFile(null);
            // Resolve existing S3 logo to a displayable URL for preview
            if (company.logo && company.logo.startsWith("public/")) {
                getUrl({ path: company.logo })
                    .then(r => setLogoPreview(r.url.href))
                    .catch(() => setLogoPreview(""));
            } else {
                setLogoPreview(company.logo || "");
            }
        }
    }, [company]);

    if (!isOpen || !company) return null;

    const update = (field: string, value: string) => setForm((prev) => ({ ...prev, [field]: value }));
    const updatePhone = (field: string, value: string) => setForm((prev) => ({ ...prev, [field]: formatPhoneNumber(value) }));

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoFile(file);
        // Local preview
        const reader = new FileReader();
        reader.onload = () => setLogoPreview(reader.result as string);
        reader.readAsDataURL(file);
    };

    return (
        <div className="uem-overlay" onClick={onClose}>
            <div className="uem-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
                <div className="uem-header">
                    <h2>Edit Company Profile</h2>
                    <button className="uem-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="uem-body">
                    <label className="uem-section-label">Company Logo</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                        <div style={{
                            width: 96, height: 96, borderRadius: 8, overflow: "hidden",
                            border: "1px solid #ccc", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            background: "#f5f5f5", flexShrink: 0,
                        }}>
                            {logoPreview ? (
                                <img src={logoPreview} alt="Logo preview" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                            ) : (
                                <span style={{ color: "#888", fontSize: 12 }}>No logo</span>
                            )}
                        </div>
                        <div style={{ flex: 1 }}>
                            <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png"
                                onChange={handleLogoChange}
                            />
                            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                                .jpg, .jpeg, or .png
                            </div>
                        </div>
                    </div>

                    <label className="uem-section-label">Company Information</label>
                    <div className="uem-field-row" style={{ marginBottom: 12 }}>
                        <div className="uem-field"><label>Company Name</label><input value={form.name || ""} onChange={(e) => update("name", e.target.value)} /></div>
                        <div className="uem-field"><label>Account #</label><input value={form.accountNumber || ""} onChange={(e) => update("accountNumber", e.target.value)} /></div>
                    </div>
                    <div className="uem-field-row" style={{ marginBottom: 12 }}>
                        <div className="uem-field"><label>Business Type</label><input value={form.businessType || ""} onChange={(e) => update("businessType", e.target.value)} /></div>
                        <div className="uem-field"><label>Primary Location</label><input value={form.primaryLocation || ""} onChange={(e) => update("primaryLocation", e.target.value)} /></div>
                    </div>
                    <div className="uem-field-row" style={{ marginBottom: 12 }}>
                        <div className="uem-field"><label>Safety Standard</label><input value={form.safetyComplianceStandard || ""} onChange={(e) => update("safetyComplianceStandard", e.target.value)} /></div>
                        <div className="uem-field"><label>Assigned Rep</label><input value={form.assignedRep || ""} onChange={(e) => update("assignedRep", e.target.value)} /></div>
                    </div>
                    <div className="uem-field-row" style={{ marginBottom: 20 }}>
                        <div className="uem-field"><label>NAICS Code</label><input value={form.industryNAICS || ""} onChange={(e) => update("industryNAICS", e.target.value)} /></div>
                        <div className="uem-field"><label>SIC Code</label><input value={form.industrySIC || ""} onChange={(e) => update("industrySIC", e.target.value)} /></div>
                    </div>

                    <label className="uem-section-label">Primary Contact</label>
                    <div className="uem-field-row" style={{ marginBottom: 12 }}>
                        <div className="uem-field"><label>Name</label><input value={form.primaryContactName || ""} onChange={(e) => update("primaryContactName", e.target.value)} /></div>
                        <div className="uem-field"><label>Position</label><input value={form.primaryContactPosition || ""} onChange={(e) => update("primaryContactPosition", e.target.value)} /></div>
                    </div>
                    <div className="uem-field-row" style={{ marginBottom: 20 }}>
                        <div className="uem-field"><label>Email</label><input type="email" value={form.primaryContactEmail || ""} onChange={(e) => update("primaryContactEmail", e.target.value)} /></div>
                        <div className="uem-field"><label>Phone</label><input value={form.primaryContactPhone || ""} onChange={(e) => updatePhone("primaryContactPhone", e.target.value)} placeholder="(xxx) xxx-xxxx" /></div>
                    </div>

                    <label className="uem-section-label">Secondary Contact</label>
                    <div className="uem-field-row" style={{ marginBottom: 12 }}>
                        <div className="uem-field"><label>Name</label><input value={form.secondaryContactName || ""} onChange={(e) => update("secondaryContactName", e.target.value)} /></div>
                        <div className="uem-field"><label>Position</label><input value={form.secondaryContactPosition || ""} onChange={(e) => update("secondaryContactPosition", e.target.value)} /></div>
                    </div>
                    <div className="uem-field-row">
                        <div className="uem-field"><label>Email</label><input type="email" value={form.secondaryContactEmail || ""} onChange={(e) => update("secondaryContactEmail", e.target.value)} /></div>
                        <div className="uem-field"><label>Phone</label><input value={form.secondaryContactPhone || ""} onChange={(e) => updatePhone("secondaryContactPhone", e.target.value)} placeholder="(xxx) xxx-xxxx" /></div>
                    </div>
                </div>
                <div className="uem-footer">
                    <button type="button" className="uem-btn-cancel" onClick={onClose}>Cancel</button>
                    <button type="button" className="uem-btn-submit" onClick={() => onSave(form, logoFile)}>Save Changes</button>
                </div>
            </div>
        </div>
    );
};
